import React from "react";
import { motion } from "framer-motion";
import { ShieldAlert, Siren, Fingerprint, ShieldCheck, Radio, Bot, Loader2 } from "lucide-react";
import { Panel, Btn, Overline } from "../ui";
import { useI18n, pickLang } from "../../lib/i18n";

const RISE_FROM = { opacity: 0, y: 12 };
const RISE_TO = { opacity: 1, y: 0 };

const SEVERITY = {
  CRITICAL: { text: "text-aegis-red", bar: "bg-aegis-red", chip: "border-aegis-red/40 bg-aegis-red/10 text-aegis-red" },
  ELEVATED: { text: "text-aegis-amber", bar: "bg-aegis-amber", chip: "border-aegis-amber/40 bg-aegis-amber/10 text-aegis-amber" },
};

export default function ExceptionCenter({ exceptions, lockdown, onLockdown, analyses, onAnalyzeThreat, onResolve }) {
  const { t, lang } = useI18n();
  const open = exceptions?.length || 0;

  return (
    <div className="space-y-6" data-testid="screen-exception">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Overline>{t("exc.overline")}</Overline>
          <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("exc.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("exc.sub")}</p>
        </div>
        <span
          className={`rounded-lg border px-3.5 py-2 font-mono text-[12px] ${
            open ? "border-aegis-red/40 bg-aegis-red/10 text-aegis-red" : "border-aegis-emerald/40 bg-aegis-emerald/10 text-aegis-emerald"
          }`}
          data-testid="exception-count"
        >
          {t("exc.open", { n: open })}
        </span>
      </div>

      <Panel
        icon={Siren}
        title={t("exc.lockdown.title")}
        subtitle={t("exc.lockdown.sub")}
        accent={lockdown ? "red" : "cyan"}
        testid="lockdown-panel"
        actions={
          <Btn
            variant={lockdown ? "emerald" : "redGhost"}
            onClick={() => onLockdown(!lockdown)}
            testid="lockdown-toggle-btn"
          >
            {lockdown ? <ShieldCheck size={15} /> : <Siren size={15} />}
            {lockdown ? t("exc.lockdown.off") : t("exc.lockdown.on")}
          </Btn>
        }
      >
        <div className="flex items-center gap-3">
          <span className={`h-2.5 w-2.5 rounded-full animate-pulseGlow ${lockdown ? "bg-aegis-red" : "bg-aegis-emerald"}`} />
          <span className={`font-mono text-[12px] ${lockdown ? "text-aegis-red" : "text-aegis-emerald"}`} data-testid="lockdown-state">
            {lockdown ? "GRID_ISOLATED" : "GRID_NOMINAL"}
          </span>
          <Radio size={14} className="ml-auto text-slate-400" />
        </div>
      </Panel>

      <Panel icon={ShieldAlert} title={t("exc.title")} subtitle={t("exc.open", { n: open })} accent="red" testid="exception-list">
        {open === 0 ? (
          <div className="rounded-lg border border-dashed border-edge py-12 text-center text-sm text-slate-400">{t("exc.empty")}</div>
        ) : (
          <div className="space-y-3">
            {exceptions.map((e, i) => {
              const sv = SEVERITY[e.severity] || SEVERITY.ELEVATED;
              const analysis = analyses?.[e.id];
              const analyzing = analysis?.status === "running";
              const analyzed = analysis?.status === "done";
              const failed = analysis?.status === "error";
              return (
                <motion.div
                  key={e.id}
                  initial={RISE_FROM}
                  animate={RISE_TO}
                  transition={{ delay: i * 0.05 }}
                  className="relative overflow-hidden rounded-lg border border-edge raise p-4 pl-5"
                  data-testid={`exception-${e.id}`}
                >
                  <span className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full ${sv.bar}`} />
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-md border px-2 py-0.5 font-mono text-[10px] tracking-wider ${sv.chip}`}>
                          {e.severity}
                        </span>
                        <span className="font-mono text-sm text-slate-100">{e.id}</span>
                        <span className="font-mono text-[11px] text-slate-400">| {e.kind}</span>
                      </div>
                      <p className={`mt-2 font-mono text-[12px] ${sv.text}`}>{e.title}</p>
                      <p className="mt-1 text-[13px] text-slate-300">{e.detail}</p>
                      <div className="mt-2.5 grid gap-1 font-mono text-[11px] text-slate-400 sm:grid-cols-2">
                        <span className="truncate">
                          {t("exc.target")}: {e.target}
                        </span>
                        <span>
                          {t("exc.detected")}: {e.detected_at}
                        </span>
                      </div>
                      <p className="mt-2.5 rounded-lg border border-edge well px-3 py-2 text-[12px] text-slate-400">
                        <span className="mr-2 font-mono text-[10px] uppercase tracking-overline text-slate-400">
                          {t("exc.remediation")}
                        </span>
                        {pickLang(e, "remediation", lang)}
                      </p>
                      {analysis && (
                        <div
                          className={`mt-2.5 rounded-lg border px-3 py-2.5 text-[12px] leading-relaxed ${
                            failed
                              ? "border-aegis-red/30 bg-aegis-red/5 text-aegis-red"
                              : "border-aegis-purple/30 bg-aegis-purple/5 text-slate-300"
                          }`}
                          data-testid={`analysis-panel-${e.id}`}
                        >
                          <span className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-overline text-aegis-purple">
                            {analyzing ? <Loader2 size={11} className="animate-spin" /> : <Bot size={11} />}
                            AEGIS · {analyzing ? "分析中…" : analyzed ? "已完成 · 待人工复核" : "分析失败"}
                          </span>
                          <p className="whitespace-pre-wrap">{analysis.text || "…"}</p>
                        </div>
                      )}
                    </div>
                    {e.requires_steward && (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Btn
                          variant="cyan"
                          onClick={() => onAnalyzeThreat?.(e)}
                          disabled={analyzing}
                          testid={`analyze-btn-${e.id}`}
                        >
                          {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
                          {analyzed ? "重新分析" : "AEGIS 分析"}
                        </Btn>
                        <Btn
                          variant={analyzed ? "emerald" : "amber"}
                          onClick={() => onResolve(e)}
                          testid={`resolve-btn-${e.id}`}
                        >
                          <Fingerprint size={15} /> {analyzed ? "复核并签名" : t("exc.resolve")}
                        </Btn>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
